import React, { MouseEventHandler, SyntheticEvent, useState } from 'react';
import classNames from 'classnames';
import { FocusedTimelineNote } from '@repo/components/notes';
import styles from './TimelineNote.module.scss';
import { PenIcon, LockIcon, PenEditedIcon, ReminderAlarmClockIcon, XIcon } from '@repo/icons';
import { ReadOnlyTextEditorContent } from '@repo/components/text-editor';
import Note, { NoteType, removeNotePrompt } from '@repo/utils/models/Note';
import { ID } from '@repo/utils/types';
import { useDispatch, useSelector } from 'react-redux';
import {
  currentlyEditingNoteSelector,
  selectRelevantNoteState,
  updateNewNoteType,
  updateNote,
  updateNoteType,
} from '@repo/redux/actions/notes';
import {
  closeNewReminderEditorInContactView,
  creatingNewNoteInContactViewSelector,
  openNewReminderEditorInContactView,
  selectNewReminderEditorOpenInContactView,
  startNewNote,
  startNewReminder,
} from '@repo/redux/actions/ui';
import { AddNoteBadge, NoteBadge, UserActionDescription } from '@repo/components/headers';
import { TimelineItemUser } from '@repo/utils/models/Timeline';
import { selectNewNoteType } from '@repo/redux/reducers/data/notes';
import { Overlay, Tooltip, SetReminderTooltip } from '@repo/components/tooltips';
import { useTheme } from '@repo/themes';
import Occasion from '@repo/utils/helpers/Occasion';

export function NewTimelineNote({
  clearNewNote,
  emptyTimeline,
}: {
  clearNewNote: (note: Note) => void;
  emptyTimeline: boolean;
}) {
  const dispatch = useDispatch();
  const note = useSelector(currentlyEditingNoteSelector);
  // manage the note type separately from the note itself for NEW notes
  const newNoteType = useSelector(selectNewNoteType);
  const creatingNewNote = useSelector(creatingNewNoteInContactViewSelector);
  const reminderEditorOpen = useSelector(selectNewReminderEditorOpenInContactView);

  const setReminderEditorOpen = (open: boolean) => {
    if (open) {
      dispatch(openNewReminderEditorInContactView);
    } else {
      dispatch(closeNewReminderEditorInContactView);
    }
  };

  return (
    // <div className={classNames(styles.noteContainer, styles.selectedNoteContainer)}>
    <div className={classNames(styles.noteContainer)}>
      {creatingNewNote ? (
        <NoteBadge
          reminder={note?.reminder}
          user={{ is_self: true }}
          noteType={newNoteType}
          updateNoteType={(noteType: NoteType) => dispatch(updateNewNoteType(noteType))}
          addingReminder={reminderEditorOpen}
        />
      ) : (
        <AddNoteBadge onClick={() => dispatch(startNewNote)} />
      )}
      <div
        className={classNames(
          styles.newNoteUserActionDescription,
          creatingNewNote && styles.active,
        )}
      >
        <UserActionDescription
          note={note}
          noteLabel={newNoteType.label}
          updateNoteLabel={(label: string) =>
            dispatch(updateNewNoteType({ ...newNoteType, label }))
          }
          user={{ is_self: true }}
          creatingNewNote={creatingNewNote}
          noteSelected
          updateReminder={(date: Occasion) => {
            dispatch(updateNote({ ...note.updateReminder(date), isReminderEditorUpdate: true }));
          }}
          removeReminder={() => {
            dispatch(updateNote({ ...note.removeReminder(), isReminderEditorUpdate: true }));
          }}
          isSettingNewReminder={reminderEditorOpen}
        />
      </div>
      <span className={styles.date}>{note?.createDateDisplay}</span>
      {!emptyTimeline && <div className={styles.line} />}
      <div className={classNames(styles.newNoteWrapper, !creatingNewNote && styles.notActive)}>
        {creatingNewNote && (
          <div className={classNames(styles.note, styles.selected, styles.newNote)}>
            <FocusedTimelineNote
              ignoreOutsideClicks
              finishEditing={() => clearNewNote(note)}
              reminderEditorOpen={reminderEditorOpen}
              setReminderEditorOpen={setReminderEditorOpen}
              isNewNote
            />
          </div>
        )}
        <div
          className={classNames(styles.newNoteTrigger, creatingNewNote && styles.inBackground)}
          onClick={() => dispatch(startNewNote)}
        >
          <SetReminderTooltip>
            <button
              className={styles.newReminderBtn}
              onClick={(e) => {
                e.stopPropagation();
                dispatch(startNewReminder);
              }}
            >
              <ReminderAlarmClockIcon width="18" height="18" />
            </button>
          </SetReminderTooltip>
        </div>
      </div>
    </div>
  );
}

interface TimelineNoteProps {
  noteID: ID;
  currentlyEditingNote?: Note;
  deleteNote?: (note: Note) => void;
  setCurrentlyEditing?: (note: Note, e: any) => void;
  clearCurrentlyEditing?: () => void;
  user: TimelineItemUser;
  isLast?: boolean;
}

export function TimelineNote({
  noteID,
  currentlyEditingNote,
  deleteNote,
  setCurrentlyEditing,
  clearCurrentlyEditing,
  user,
  isLast,
}: TimelineNoteProps) {
  const dispatch = useDispatch();
  const [theme] = useTheme();
  const note = useSelector(selectRelevantNoteState(noteID));
  const [truncated, setTruncated] = useState(true);
  const [selectingIconAndColor, setSelectingIconAndColor] = useState(false);
  const [editingReminder, setEditingReminder] = useState(false);

  const editableNoteActions = (
    <>
      <button className="btn-none" type="button">
        <PenIcon width={20} height={20} hoverOpacity />
      </button>
      <button onClick={removeNotePrompt(() => deleteNote(note))} className="btn-none" type="button">
        <XIcon width={15} height={15} hoverOpacity />
      </button>
    </>
  );

  const readOnlyNoteActions = (
    <Tooltip placement="right" overlay={<Overlay>Read-only</Overlay>}>
      <LockIcon
        width="16"
        height="18"
        viewBox="7.5 7 9 10"
        customColor={theme === 'light' ? '#bbb' : '#999'}
      />
    </Tooltip>
  );

  const handleSelect: MouseEventHandler = (e) => {
    // we want to allow the user to select note text for copy and paste without
    // selecting the note for editing purposes.
    const selectedText: Selection = window.getSelection();

    if (e && selectedText) {
      const someTextSelected =
        (selectedText?.focusOffset || 0) != (selectedText?.anchorOffset || 0);
      const isInTarget = (e.target as HTMLDivElement).contains(
        selectedText?.anchorNode?.parentNode,
      );

      if (someTextSelected && isInTarget) {
        return;
      }
    }

    if (truncated) {
      setTruncated(false);
    }

    if (note.isReadOnly) {
      return;
    }

    setCurrentlyEditing(note, e);
  };

  const handleBlur = (e?: SyntheticEvent) => {
    e?.preventDefault();
    clearCurrentlyEditing();
  };

  const renderSelectedNote = () => (
    <div className={classNames(styles.note, styles.selected)}>
      <FocusedTimelineNote onBlur={handleBlur} />
    </div>
  );

  const renderNote = () => (
    <div onClick={handleSelect} className={styles.note}>
      <div className={classNames(styles.content, truncated && styles.truncated)}>
        <ReadOnlyTextEditorContent
          value={note.content}
          updated={note.updated}
          followupOccasion={note.reminder?.date}
          clickableMentions
        />
      </div>
      <div className={styles.actions}>
        {note.isReadOnly ? readOnlyNoteActions : editableNoteActions}
      </div>
    </div>
  );

  if (!note) {
    return null;
  }

  const selected = note.isEqual(currentlyEditingNote);
  const hideNote = !selected && note?.content?.trim() === '' && !!note?.reminder;

  let noteDisplay = null;
  if (!hideNote && selected) {
    noteDisplay = renderSelectedNote();
  } else if (!hideNote && !selected) {
    noteDisplay = renderNote();
  }

  return (
    <div
      className={classNames(
        styles.noteContainer,
        (selected || selectingIconAndColor || editingReminder) && styles.selectedNoteContainer,
        'timeline-note',
      )}
    >
      <NoteBadge
        reminder={note.reminder}
        readOnly={note.isReadOnly}
        user={user}
        setSelectingIconAndColor={setSelectingIconAndColor}
        noteType={note.type}
        updateNoteType={(noteType: NoteType) => dispatch(updateNoteType({ noteID, noteType }))}
      />
      <UserActionDescription
        note={note}
        user={user}
        noteLabel={note.type.label}
        updateNoteLabel={(label: string) =>
          dispatch(updateNoteType({ noteID, noteType: { ...note.type, label } }))
        }
        updateReminder={(date: Occasion) => {
          dispatch(updateNote({ ...note.updateReminder(date), isReminderEditorUpdate: true }));
        }}
        removeReminder={() => {
          if (!note.content) {
            deleteNote(note);
          } else {
            dispatch(updateNote({ ...note.removeReminder(), isReminderEditorUpdate: true }));
          }
        }}
        setEditingReminder={setEditingReminder}
        showEditNoteBtn={hideNote}
        noteSelected={selected}
        selectNote={(e) => {
          setCurrentlyEditing(note, e);
        }}
      />
      <span className={styles.date}>
        {note?.updated && note?.updatedMoreThanOneDayAgo && (
          <Tooltip placement="left" overlay={<Overlay>Edited {note.updateDateDisplay}</Overlay>}>
            <PenEditedIcon hoverOpacity style={{ cursor: 'default' }} />
          </Tooltip>
        )}
        {note.createDateDisplay}
      </span>
      {!isLast && <div className={styles.line} />}
      {noteDisplay}
    </div>
  );
}
